import React from 'react';
import {
  WORKFLOW_LIFECYCLE_STATUSES,
  isWorkflowLifecycleStatus,
} from 'src/app/workflows/workflowLifecycle';

export interface IWorkflowStepLike {
  label?: string;
  buttonColor?: string;
}

export interface IWorkflowLike {
  steps?: IWorkflowStepLike[];
}

/**
 * @registry Status-chip filter state for workflow-driven job lists (Changes/RFIs/Submittals). Builds the filter labels from the job's current workflow template steps (in template order) plus lifecycle statuses, and collapses any legacy status not covered by that template into a single 'Other' bucket. Exposes the selected filter, the filtered items, and the covered-status Set (feed it to useWorkflowStatusSegments so 'Other' counts match 'Other' filtering). Pass extraStatuses for step-less statuses a module produces at stepIndex -1 (e.g. RFI 'More Information Needed') so they render as their own filter block instead of collapsing into 'Other'. Use on any list of items that carry a status, passing the job's current workflow.
 */
export function useWorkflowStatusFilters<T extends { status?: string }>(
  items: T[],
  currentWorkflow?: IWorkflowLike,
  extraStatuses: readonly string[] = [],
) {
  const [statusFilter, setStatusFilter] = React.useState('All');

  // Callers commonly pass a fresh array literal (Object.keys(...)) or omit the
  // arg (new [] default) each render; the returned filters/covered/filteredItems
  // must stay referentially stable across renders so a consumer feeding
  // filteredItems to MantineReactTable as `data` does not signal a phantom
  // data change on every render.
  const extraStatusesKey = extraStatuses.join('\u0001');
  const stableExtraStatuses = React.useMemo(
    () => [...extraStatuses],
    [extraStatusesKey],
  );

  const { filters, covered } = React.useMemo(() => {
    const stepLabels: string[] = [];
    const seen = new Set<string>();
    for (const step of currentWorkflow?.steps ?? []) {
      const label = step.label;
      if (label && !seen.has(label) && !isWorkflowLifecycleStatus(label)) {
        seen.add(label);
        stepLabels.push(label);
      }
    }

    const extras = stableExtraStatuses.filter(
      (s) => !stepLabels.includes(s) && !isWorkflowLifecycleStatus(s),
    );

    const coveredSet = new Set<string>([
      ...stepLabels,
      ...extras,
      ...WORKFLOW_LIFECYCLE_STATUSES,
    ]);

    // A template step literally named 'Other' wins the label and filters by
    // exact status match; the pseudo-bucket is suppressed to avoid collision.
    const hasOther =
      !stepLabels.includes('Other') &&
      items.some((i) => i.status && !coveredSet.has(i.status));

    return {
      filters: [
        'All',
        'Draft',
        ...stepLabels,
        ...extras,
        'Rejected',
        'Voided',
        ...(hasOther ? ['Other'] : []),
      ],
      covered: coveredSet,
    };
  }, [items, currentWorkflow, stableExtraStatuses]);

  const selectableStatuses = React.useMemo(
    () => filters.filter((f) => f !== 'All' && f !== 'Other'),
    [filters],
  );

  const filteredItems = React.useMemo(() => {
    if (statusFilter === 'All') return items;
    if (statusFilter === 'Other' && !covered.has('Other')) {
      return items.filter((item) => item.status && !covered.has(item.status));
    }
    return items.filter((item) => item.status === statusFilter);
  }, [items, statusFilter, covered]);

  return {
    filters,
    covered,
    selectableStatuses,
    statusFilter,
    setStatusFilter,
    filteredItems,
  };
}
